"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useSyncExternalStore } from "react";
export type WaveViewMode = "chat" | "gallery";
const STORAGE_KEY = "waveViewModes";
const STORAGE_EVENT = "waveViewModesChange";
type WaveViewModes = Record<string, WaveViewMode>;
const EMPTY_MODES: WaveViewModes = {};
let memoryModes: WaveViewModes = {};
let useMemoryStore = false;

// Cache for useSyncExternalStore - must return same reference when data unchanged
let cachedModes: WaveViewModes = EMPTY_MODES;
let cachedRaw: string | null = null;
const readStoredModes = (): WaveViewModes => {
  if (typeof window === "undefined") {
    return EMPTY_MODES;
  }
  if (useMemoryStore) {
    return memoryModes;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    // Return cached if raw string unchanged
    if (stored === cachedRaw) {
      return cachedModes;
    }
    if (!stored) {
      cachedRaw = null;
      cachedModes = EMPTY_MODES;
      return EMPTY_MODES;
    }
    try {
      cachedRaw = stored;
      cachedModes = JSON.parse(stored) as WaveViewModes;
      return cachedModes;
    } catch {
      cachedRaw = null;
      cachedModes = EMPTY_MODES;
      return EMPTY_MODES;
    }
  } catch {
    useMemoryStore = true;
    return memoryModes;
  }
};
const writeStoredModes = (next: WaveViewModes) => {
  memoryModes = next;
  if (typeof window === "undefined") {
    return;
  }
  if (!useMemoryStore) {
    try {
      const serialized = JSON.stringify(next);
      window.localStorage.setItem(STORAGE_KEY, serialized);
      // Sync cache
      cachedRaw = serialized;
      cachedModes = next;
    } catch (e) {
      useMemoryStore = true;
      console.warn("Error saving wave view modes to localStorage:", e);
    }
  }
  window.dispatchEvent(new Event(STORAGE_EVENT));
};
const subscribeToModes = (onStoreChange: () => void) => {
  if (typeof window === "undefined") {
    return () => {};
  }
  const handler = (event: Event) => {
    if (event instanceof StorageEvent && event.key !== null && event.key !== STORAGE_KEY) {
      return;
    }
    onStoreChange();
  };
  window.addEventListener("storage", handler);
  window.addEventListener(STORAGE_EVENT, handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(STORAGE_EVENT, handler);
  };
};
export function useWaveViewMode(waveId) {
  const $ = _c(8);
  const allModes = useSyncExternalStore(subscribeToModes, readStoredModes, _temp);
  const viewMode = allModes[waveId] ?? "chat";
  let t0;
  if ($[0] !== waveId) {
    t0 = mode => {
      const prev = readStoredModes();
      const current = prev[waveId] ?? "chat";
      const next = typeof mode === "function" ? mode(current) : mode;
      writeStoredModes({
        ...prev,
        [waveId]: next
      });
    };
    $[0] = waveId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setViewMode = t0;
  let t1;
  if ($[2] !== setViewMode) {
    t1 = () => {
      setViewMode(_temp2);
    };
    $[2] = setViewMode;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const toggleViewMode = t1;
  let t2;
  if ($[4] !== setViewMode || $[5] !== toggleViewMode || $[6] !== viewMode) {
    t2 = {
      viewMode,
      setViewMode,
      toggleViewMode
    };
    $[4] = setViewMode;
    $[5] = toggleViewMode;
    $[6] = viewMode;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp2(prev_0) {
  return prev_0 === "chat" ? "gallery" : "chat";
}
function _temp() {
  return EMPTY_MODES;
}